import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorOptions,
	CreatorRendererOptions,
	CreatorState,
	SavedCreatorState,
} from '@/types/creator';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { isDev } from '@/components/App';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { ThreeEvent, ThreeEvents } from '@/renderer/types';
import { ProductModuleCollection } from '@/api/ProductModuleCollectionApi';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { Project } from '@/api/ProjectApi';
import { CreatorAction, UUID } from '@/types/types';
import { groupAttributeValues, roundToHalf } from '@/utils/utilities';
import { AttributeValue } from '@/api/AttributeValueApi';
import { current } from 'immer';
import { Preset, PresetStatus } from '@/api/PresetApi';
import { pick } from 'lodash';
import { ProductModule } from '@/api/ProductModuleApi';
import APP_CONFIG from '@/configs/appConfig';

const renderer = ProjectRenderer.getInstance();

const initialState: CreatorState = {
	project: {
		id: '' as UUID,
		price: 0,
		creatorProductModules: [],
		createdAt: '',
		updatedAt: '',
	},
	preset: {
		id: '' as UUID,
		name: '',
		price: 0,
		listOrder: 0,
		creatorProductModules: [],
		status: PresetStatus.INACTIVE,
		createdAt: '',
		updatedAt: '',
		options: {},
	},
	selectedPreset: null,
	selectedProductModuleCollection: null,
	attributesWithValues: [],
	globalSelectedAttributeValues: {},
	renderer: {
		cameraDistance: RENDERER_CONFIG.INITIAL_ZOOM,
		loading: false,
	},
	options: {
		isPresetMode: false,
		isOpen: isDev,
		lockedPresetId: null,
		syncProjectCounter: 0,
		isLoading: false,
		isMetersVisible: false,
		applyValueToAll: false,
		selectedSlot: null,
		selectedCreatorProductModule: null,
		swapDetails: {
			creatorProductModule: null,
			slotTypes: [],
			modulesIds: [],
		},
		summaryDrawerOpened: false,
		downloadDrawerOpened: false,
		helpDrawerOpened: false,
		helpDrawerData: null,
		instructionModalOpened: false,
		moduleContextMenuData: {
			moduleId: null,
			position: [0, 0],
			deleteable: false,
			swapDetails: {
				slotTypes: [],
				modulesIds: [],
			},
		},
	},
	snapshots: [],
};

function withSnapshot(
	state: CreatorState,
	updater: (state: CreatorState) => void
) {
	const modules = state.options.isPresetMode
		? state.preset.creatorProductModules
		: state.project.creatorProductModules;

	if (!modules.length) {
		updater(state);
		return;
	}

	state.snapshots.push({
		modules: current(modules),
		attributesWithValues: current(state.attributesWithValues),
		selectedProductModuleCollection: current(
			state.selectedProductModuleCollection
		),
		globalSelectedAttributeValues: current(state.globalSelectedAttributeValues),
	});
	if (state.snapshots.length > APP_CONFIG.HISTORY_MAX_LENGTH)
		state.snapshots.shift();

	updater(state);
}

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		createFromPreset: (
			state,
			{ payload }: PayloadAction<{ project: Project; preset: Preset }>
		) => {
			state.selectedPreset = payload.preset;

			state.project.creatorProductModules =
				payload.project.creatorProductModules;
			state.project.price = payload.project.price;

			let attributeId: UUID;
			for (attributeId in state.globalSelectedAttributeValues) {
				state.globalSelectedAttributeValues[attributeId] = null;
			}

			for (const attributeValue of state.project.creatorProductModules[0]
				.selectedAttributeValues) {
				if (
					state.project.creatorProductModules.every((mod) =>
						mod.selectedAttributeValues.some(
							(attr) => attr.attribute.id === attributeValue.attribute.id
						)
					)
				) {
					state.globalSelectedAttributeValues[attributeValue.attribute.id] =
						attributeValue;
				}
			}
		},
		loadProject: (state, { payload }: PayloadAction<SavedCreatorState>) => {
			state.project = payload.project;
			state.selectedProductModuleCollection =
				payload.selectedProductModuleCollection;
			state.selectedPreset = payload.selectedPreset;
			state.attributesWithValues = payload.attributesWithValues;
			state.globalSelectedAttributeValues =
				payload.globalSelectedAttributeValues;

			Object.assign(state.options, pick(payload.options, ['lockedPresetId']));
		},
		loadPreset: (state, { payload }: PayloadAction<Preset>) => {
			state.preset = payload;
			state.options.isPresetMode = true;
		},
		updateProject: (state, { payload }: PayloadAction<Partial<Project>>) => {
			Object.assign(state.project, payload);
		},
		updatePreset: (state, { payload }: PayloadAction<Partial<Preset>>) => {
			Object.assign(state.preset, payload);
		},
		syncProject: (state, { payload }: PayloadAction<Project>) => {
			state.project.price = payload.price;
			state.project.updatedAt = payload.updatedAt;

			for (const creatorProductModule of state.project.creatorProductModules) {
				const match = payload.creatorProductModules.find(
					(mod) => mod.id === creatorProductModule.id
				);

				if (!match) continue;

				creatorProductModule.code = match.code;
				creatorProductModule.price = match.price;
			}
		},
		syncPreset: (state, { payload }: PayloadAction<Preset>) => {
			state.preset.price = payload.price;
			state.preset.updatedAt = payload.updatedAt;

			for (const creatorProductModule of state.preset.creatorProductModules) {
				const match = payload.creatorProductModules.find(
					(mod) => mod.id === creatorProductModule.id
				);

				if (!match) continue;
				creatorProductModule.code = match.code;
			}
		},
		selectCollection: (
			state,
			{ payload }: PayloadAction<ProductModuleCollection>
		) => {
			if (state.selectedProductModuleCollection?.id === payload.id) return;

			state.selectedPreset = null;

			state.globalSelectedAttributeValues =
				payload.defaultAttributeValues.reduce<
					CreatorState['globalSelectedAttributeValues']
				>((acc, curr) => {
					acc[curr.attribute.id] = curr;
					return acc;
				}, {});

			state.selectedProductModuleCollection = payload;
			state.attributesWithValues = groupAttributeValues(
				payload.attributeValues
			);
		},
		addCreatorProductModule: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			withSnapshot(state, () => {
				if (state.options.isPresetMode)
					state.preset.creatorProductModules.push(payload);
				else state.project.creatorProductModules.push(payload);
			});
		},
		removeCreatorProductModule: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			withSnapshot(state, () => {
				if (state.options.isPresetMode)
					state.preset.creatorProductModules =
						state.preset.creatorProductModules.filter(
							(mod) => mod.id !== payload.id
						);
				else
					state.project.creatorProductModules =
						state.project.creatorProductModules.filter(
							(mod) => mod.id !== payload.id
						);

				state.options.selectedCreatorProductModule = null;
			});
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		handleRendererEvent: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: ThreeEvent;
				data: ThreeEvents[ThreeEvent];
			}>
		) => {
			const { type } = payload;

			switch (type) {
				case ThreeEvent.SLOT_SELECTION: {
					const data = payload.data as ThreeEvents[ThreeEvent.SLOT_SELECTION];
					state.options.selectedSlot = data;

					state.options.selectedCreatorProductModule = null;
					break;
				}
				case ThreeEvent.MODULE_SELECTION: {
					const data = payload.data as ThreeEvents[ThreeEvent.MODULE_SELECTION];
					if (!data) {
						state.options.selectedCreatorProductModule = null;
						return;
					}

					const modules = state.options.isPresetMode
						? state.preset.creatorProductModules
						: state.project.creatorProductModules;

					const creatorProductModule = modules.find(
						(mod) => mod.id === data.id
					);
					if (!creatorProductModule) return;

					state.options.selectedCreatorProductModule = {
						...creatorProductModule,
						deleteable: data.deleteable,
					};
					break;
				}
				case ThreeEvent.DESELECT_ALL: {
					state.options.selectedSlot = null;

					state.options.selectedCreatorProductModule = null;
					break;
				}
				case ThreeEvent.UPDATE_CAMERA_DISTANCE: {
					state.renderer.cameraDistance = roundToHalf(payload.data as number);
					break;
				}
				case ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER: {
					const data =
						payload.data as ThreeEvents[ThreeEvent.SET_CREATOR_PRODUCT_MODULES_ORDER];

					data.forEach((id, idx) => {
						const creatorProductModule = state.options.isPresetMode
							? state.preset.creatorProductModules.find((mod) => mod.id === id)
							: state.project.creatorProductModules.find(
									(mod) => mod.id === id
								);
						if (!creatorProductModule) return;

						creatorProductModule.listOrder = idx;
					});
					break;
				}
				case ThreeEvent.CONTEXT_MENU: {
					const data = payload.data as ThreeEvents[ThreeEvent.CONTEXT_MENU];
					Object.assign(state.options.moduleContextMenuData, data);
					break;
				}
				default:
					throw new Error('UNKNOW_EVENT');
			}
		},
		toogleHelpDrawer: (
			state,
			{
				payload,
			}: PayloadAction<
				Pick<CreatorOptions, 'helpDrawerOpened' | 'helpDrawerData'>
			>
		) => {
			Object.assign(state.options, payload);
		},
		toogleInstructionModal: (
			state,
			{
				payload,
			}: PayloadAction<
				Pick<CreatorOptions, 'helpDrawerOpened' | 'helpDrawerData'>
			>
		) => {
			Object.assign(state.options, payload);
		},
		handleCreatorProductModuleConfiguration: (
			state,
			{
				payload,
			}: PayloadAction<{
				creatorProductModule: CreatorProductModule;
				attributeValues: AttributeValue[];
			}>
		) => {
			const modules = state.options.isPresetMode
				? state.preset.creatorProductModules
				: state.project.creatorProductModules;

			const creaProdMod = modules.find(
				(mod) => mod.id === payload.creatorProductModule.id
			);
			if (!creaProdMod) return;

			withSnapshot(state, () => {
				for (const attributeValue of payload.attributeValues) {
					const oldValue = creaProdMod.selectedAttributeValues.find(
						(attr) => attr.attribute.id === attributeValue.attribute.id
					);

					if (oldValue)
						creaProdMod.selectedAttributeValues =
							creaProdMod.selectedAttributeValues.filter(
								(val) => val.id !== oldValue.id
							);

					creaProdMod.selectedAttributeValues.push(attributeValue);
					Object.assign(
						state.options.selectedCreatorProductModule!,
						creaProdMod
					);

					if (
						state.globalSelectedAttributeValues[attributeValue.attribute.id]
							?.id !== attributeValue.id
					)
						state.globalSelectedAttributeValues[attributeValue.attribute.id] =
							null;
				}
			});
		},
		handleCreatorProductModuleSwap: (
			state,
			{
				payload,
			}: PayloadAction<{
				creatorProductModuleId: UUID;
				productModule: ProductModule;
			}>
		) => {
			const modules = state.options.isPresetMode
				? state.preset.creatorProductModules
				: state.project.creatorProductModules;

			const creaProdMod = modules.find(
				(mod) => mod.id === payload.creatorProductModuleId
			);
			if (!creaProdMod) return;

			state.options.swapDetails = {
				creatorProductModule: null,
				slotTypes: [],
				modulesIds: [],
			};

			withSnapshot(state, () => {
				creaProdMod.productModule = payload.productModule;
			});
		},
		handleCollectionAttributeValuesChange: (
			state,
			{
				payload,
			}: PayloadAction<
				{
					attributeId: UUID;
					attributeValue: AttributeValue | null;
				}[]
			>
		) => {
			withSnapshot(state, () => {
				for (const change of payload) {
					state.globalSelectedAttributeValues[change.attributeId] =
						change.attributeValue;

					const modules = state.options.isPresetMode
						? state.preset.creatorProductModules
						: state.project.creatorProductModules;

					for (const creaProdMod of modules) {
						const oldValue = creaProdMod.selectedAttributeValues.find(
							(attr) => attr.attribute.id === change.attributeId
						);

						if (oldValue)
							creaProdMod.selectedAttributeValues =
								creaProdMod.selectedAttributeValues.filter(
									(val) => val.id !== oldValue.id
								);

						if (change.attributeValue)
							creaProdMod.selectedAttributeValues.push(change.attributeValue);
					}
				}

				const actions = payload.reduce<CreatorAction[]>((acc, curr) => {
					if (curr.attributeValue) acc.push(...curr.attributeValue.actions);
					return acc;
				}, []);

				renderer.handleActions(actions);
			});
		},
		refreshAPIData: (state) => {
			state.options.syncProjectCounter++;
		},
		undo: (state) => {
			const snapshot = state.snapshots.pop();
			if (!snapshot) return;

			if (state.options.isPresetMode)
				state.preset.creatorProductModules = snapshot.modules;
			else state.project.creatorProductModules = snapshot.modules;

			state.selectedProductModuleCollection =
				snapshot.selectedProductModuleCollection;
			state.attributesWithValues = snapshot.attributesWithValues;
			state.globalSelectedAttributeValues =
				snapshot.globalSelectedAttributeValues;

			renderer.undo();
		},
		setCreatorProductModuleToSwap: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			state.options.swapDetails = {
				creatorProductModule: payload,
				...state.options.moduleContextMenuData.swapDetails,
			};
		},
		closeContextMenu: (state) => {
			state.options.moduleContextMenuData = {
				moduleId: null,
				position: [0, 0],
				deleteable: false,
				swapDetails: {
					slotTypes: [],
					modulesIds: [],
				},
			};
		},
		resetCreator: (state) => {},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
